import React, { useEffect, useState } from "react";
import { SortableContainer, SortableContainerProps, SortableElement, SortableElementProps } from "react-sortable-hoc";
import { arrayMoveImmutable } from "array-move";
import { intl } from "LocaleWrapper";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, Card } from "@cofano/cck";
import { CustomDialog } from "components/dialogs/Dialogs";
import { Language } from "utils";
import ShipSelect from "view/ships/components/selects/ShipSelect";
import { gql } from "__generated__/gql";
import { useLazyQuery, useMutation } from "@apollo/client";
import { IconButton, useTheme } from "@mui/material";
import { Ship as GraphQlShip } from "__generated__/graphql";

type Ship = Pick<GraphQlShip, "id" | "name" | "sequenceNumber">;

type UpdateShipOrderDialogProps = {
	open: boolean;
	onClose: () => void;
	refetch: () => void;
};

const UpdateShipOrderDialog = ({ open, onClose, refetch }: UpdateShipOrderDialogProps) => {

	const [ sequence, setSequence ] = useState<Ship[]>([]);

	const theme = useTheme();

	const save = async () => {
		await saveSequence({
			variables: {
				sequence: sequence.sort(ship => ship.sequenceNumber ?? 0).map((s) => ({ id: s.id })),
			},
		});
		refetch();
		onClose();
	};

	const [ querySequence ] = useLazyQuery(query_ship_sequence, {
		onCompleted: (ships) => {
			setSequence(ships.shipsWithSequence);
		},
	});

	const [ saveSequence, { loading } ] = useMutation(mutation_ship_sequence);

	const onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number, newIndex: number }) => {
		setSequence(arrayMoveImmutable([ ...sequence ], oldIndex, newIndex)
			.map((s, index) => ({ ...s, sequenceNumber: index })),
		);
	};

	const deleteShipFromSequence = (shipId: number) => {
		setSequence(sequence.filter(s => s.id !== shipId)
			.map((s, index) => ({ ...s, sequenceNumber: index })));
	};

	const SortableItem: React.ComponentClass<SortableElementProps & { ship: Ship; deleteShipFromSequence: (shipId: number) => void }> = (
		SortableElement(({ ship, deleteShipFromSequence }: { ship: Ship; deleteShipFromSequence: (shipId: number) => void }) => (
			<div
				className="sortable-item"
				style={{
					backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#FFF",
					borderBottom: theme.palette.mode === "dark" ? "1px solid rgba(0, 0, 0, 0.87)" : "1px solid #F0F0F0",
					color: theme.palette.mode === "dark" ? "#fff" : "#333",
					display: "flex",
					flexGrow: 1,
				}}>
				<div style={{ whiteSpace: "nowrap", display: "flex", flexGrow: 1, alignItems: "center", justifyContent: "space-between" }}>
					<span>{ship.name}</span>

					<IconButton
						size='small'
						className="delete-button"
						onClick={() => deleteShipFromSequence(ship.id)}
					>
						<DeleteIcon fontSize='small' />
					</IconButton>
				</div>
			</div>
		))
	);

	const SortableList: React.ComponentClass<SortableContainerProps & { sequence: Ship[]; deleteShipFromSequence: (shipId: number) => void }> = (
		SortableContainer(({ sequence, deleteShipFromSequence }: { sequence: Ship[]; deleteShipFromSequence: (shipId: number) => void }) => (
			<div
				className="sortable-list"
				style={{
					backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#F3F3F3",
					border: theme.palette.mode === "dark" ? "1px solid rgba(0, 0, 0, 0.87)" : "1px solid #E5E5E5",
				}}>
				{sequence
					.sort((a, b) => a.sequenceNumber! - b.sequenceNumber!)
					.map((ship, index: number) => <SortableItem
						key={ship.id}
						index={index}
						ship={ship}
						deleteShipFromSequence={deleteShipFromSequence}
					/>)}
			</div>
		))
	);

	useEffect(() => {
		if (open) {
			querySequence();
		}
	}, [ open ]);

	return (
		<CustomDialog
			open={open}
			title={Language("CHANGE_ORDER")}
			onClose={onClose}
			maxWidth="sm"
			dialogStyling={{ content: { backgroundColor: theme.palette.mode === "dark" ? "inherit" : "#EEE" } }}
			actions={(
				<Button
					loading={loading}
					onClick={async () => await save()}
					size="small"
				>
					{intl.formatMessage({ id: "SAVE" })}
				</Button>
			)}>
			<div className="change-status-order-dialog" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
				<Card>
					<SortableList
						sequence={sequence}
						onSortEnd={onSortEnd}
						deleteShipFromSequence={deleteShipFromSequence}
						shouldCancelStart={(e) => {
							// Cancel drag if clicking on delete button or its children
							const target = e.target as Element;
							return target.closest(".delete-button") !== null;
						}}
						helperClass={"sortable-status-helper"} />

					<div style={{ paddingLeft: 2, paddingRight: 2, paddingTop: 8 }}>
						<ShipSelect
							getOptionDisabled={(ship) => sequence.map(s => s.id).includes(ship.id)}
							multiple={false}
							value={null}
							onChange={(ship) => {
								if (ship) {
									setSequence([ ...sequence, ship ]);
								}
							}}
						/>
					</div>
				</Card>
			</div>
		</CustomDialog>
	);
};

const query_ship_sequence = gql(`#graphql
query ShipSequenceQuery {
	shipsWithSequence {
		id
		name
		sequenceNumber
	}
}
`);

const mutation_ship_sequence = gql(`#graphql
mutation ShipSequenceMutation($sequence: [ShipInput!]!) {
	changeShipSequence(sequence: $sequence)
}
`);

export default UpdateShipOrderDialog;
